<template>
  <CustomizationFunnel
    :breadcrumb
    :is-services-pickers-response-pending
    :pickers="pickersResponse"
    :pickers-services="pickersServices?.pickerGroups ?? []"
    :price
    :price-without-subsidies
    :price-without-trade-in
    :product
    :product-tracking
    :selected-mobile-plan
    :selected-offer
    :technical-specifications
    :upgrade-picker-fields="upgradeFields"
  >
    <template #sticky-bar="{ currentStep, totalSteps, selectedOptions }">
      <RevPortal defer :to="`#${STICKY_BARS_PORTAL_TARGET}`">
        <StickyBarTop
          id="sticky-bar-top"
          :price
          :price-without-subsidies
          :product
          :selected-mobile-plan
          :selected-offer
          :selected-options
          :tracking="productTracking"
          :upgrade-picker-fields="upgradeFields"
        >
          <template #progress-bar>
            <ProgressBar :current-step :total-steps />
          </template>
        </StickyBarTop>

        <StickyBarTopSmall
          :select="selectedOffer.shipping"
          :selected-options
          :shipping="selectedOffer.shipping"
          :upgrade-picker-fields="upgradeFields"
        >
          <template #progress-bar>
            <ProgressBar :current-step :total-steps />
          </template>
        </StickyBarTopSmall>
        <ClientOnly>
          <UpgradeForLess
            :pickers="pickersResponse"
            :product
            @upgrade-accepted="handleUpgradeAccepted"
          />
        </ClientOnly>
      </RevPortal>
    </template>
    <template #details>
      <div>
        <Accessories
          class="mb-32"
          :comes-with="product.comesWith"
          :partner-promo-codes="product.includedServiceOffers.partnerPromoCodes"
        />

        <!-- TODO after we roll out M&M on all cats -->
        <!-- Should be above reassurance items in the proposal or after reassurance when no pickers -->
        <SellerComment
          v-if="selectedOffer && selectedOffer.sellerComment"
          :comment="selectedOffer.sellerComment"
        />

        <AdditionalInfo
          v-if="
            technicalSpecifications &&
            selectedOffer &&
            selectedOffer.offerLegacyId
          "
          data-qa="customization-funnel-wrapper"
          :instructions="product.instructions"
          :listing-id="selectedOffer.offerLegacyId"
          :product-title="product.titles.default"
          :technical-specifications
        />
      </div>
    </template>
  </CustomizationFunnel>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue'

import type { Price } from '@backmarket/http-api'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import type { GetBreadcrumbResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/breadcrumb'
import type {
  GetPickersResponse,
  GetServicesPickersResponse,
} from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { GetTechnicalSpecificationsResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/technical-specifications'
import { RevPortal } from '@ds/components/Portal'

import { ClientOnly } from '#components'

import Accessories from '~/scopes/product-core/components/presentationals/Accessories/Accessories.vue'
import AdditionalInfo from '~/scopes/product-core/components/presentationals/AdditionalInfo/AdditionalInfo.vue'
import SellerComment from '~/scopes/product-core/components/presentationals/SellerComment/SellerComment.vue'

import { useProductTracking } from '../../composables/useProductTracking'
import { STICKY_BARS_PORTAL_TARGET } from '../../constants'
import CustomizationFunnel from '../CustomizationFunnel/CustomizationFunnel.vue'
import ProgressBar from '../CustomizationFunnel/components/ProgressBar/ProgressBar.vue'
import StickyBarTop from '../CustomizationFunnel/components/StickyBarTop/StickyBarTop.vue'
import StickyBarTopSmall from '../CustomizationFunnel/components/StickyBarTopSmall/StickyBarTopSmall.vue'
import UpgradeForLess from '../CustomizationFunnel/components/UpgradeForLess/UpgradeForLess.vue'

const props = defineProps<{
  breadcrumb?: GetBreadcrumbResponse | null | undefined
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  pickersResponse: GetPickersResponse
  pickersServices: GetServicesPickersResponse | undefined
  isServicesPickersResponsePending: boolean
  price: Price
  priceWithoutSubsidies?: Price | null
  priceWithoutTradeIn?: Price | null
  product: GetProductResponse
  selectedMobilePlan?: MobilePlanOffer
  productTracking: ReturnType<typeof useProductTracking>
  technicalSpecifications: GetTechnicalSpecificationsResponse | undefined
}>()

const upgradeFields = ref<
  NonNullable<GetPickersResponse['upgrade']>['pickerFields'] | undefined
>(undefined)

function handleUpgradeAccepted(
  pickerFields?: NonNullable<GetPickersResponse['upgrade']>['pickerFields'],
) {
  upgradeFields.value = pickerFields
}

watch(
  () => props.pickersResponse,
  (_newValue, oldValue) => {
    // !oldValue.upgrade means that the previous selection was the upgraded one
    if (!oldValue.upgrade && upgradeFields.value) {
      upgradeFields.value = undefined
    }
  },
)
</script>
